import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeacherTrialStats {
  teacher_code: string;
  teacher_name: string;
  teacher_nickname: string;
  total_trials: number;
  successful_trials: number;
  success_rate: number;
}

interface TrialCourseRow {
  teacher_code: string;
  name: string;
  nickname: string;
  status: string;
  month: number;
  year: number;
  class_date: string;
}

export interface TrialStatsFilters {
  startDate?: string;  // ISO format: 2025-01-01
  endDate?: string;    // ISO format: 2025-01-31
  status?: string;
}

export function useTeacherTrialStats(filters?: TrialStatsFilters) {
  return useQuery({
    queryKey: ['teacher-trial-stats', filters?.startDate, filters?.endDate, filters?.status],
    queryFn: async () => {
      let query = supabase.from('v_trial_courses').select('teacher_code, name, nickname, status, month, year, class_date');
      
      if (filters?.startDate) {
        query = query.gte('class_date', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('class_date', filters.endDate);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      if (!data) return [];

      // Group by teacher and calculate stats
      const grouped = (data as TrialCourseRow[]).reduce((acc, row) => {
        const key = row.teacher_code;
        if (!acc[key]) {
          acc[key] = {
            teacher_code: row.teacher_code,
            teacher_name: row.name || '',
            teacher_nickname: row.nickname || '',
            total_trials: 0,
            successful_trials: 0,
            success_rate: 0,
          };
        }
        
        acc[key].total_trials += 1;
        if (row.status === 'ปิดการขายเรียบร้อย') {
          acc[key].successful_trials += 1;
        }
        
        return acc;
      }, {} as Record<string, TeacherTrialStats>);

      // Calculate success rate for each teacher
      const result = Object.values(grouped).map(teacher => ({
        ...teacher,
        success_rate: teacher.total_trials > 0 
          ? (teacher.successful_trials / teacher.total_trials) * 100 
          : 0,
      }));

      // Sort by total trials descending
      return result.sort((a, b) => b.total_trials - a.total_trials);
    }
  });
}
